// Page de réponse à une pré-demande de dérogation (D-083, maquettes B3 validées) : /d/<jeton>,
// SANS compte, au vouvoiement, aux couleurs du club qui demande. L'autre club choisit un créneau
// (« D'accord pour ce créneau »), « Pas possible » ou « Proposer autre chose » (une seule
// contre-proposition), dit qui il est, et envoie. /d/<jeton>?stop=1 : « Ne plus recevoir ces messages ».
// D-146 (maquettes p4 validées) : « Les prévenir » : le changement annoncé (« nous allons saisir ce changement sur FBI »),
// « Bien reçu » en un geste, ou « Ça nous pose problème » avec un mot ; nom et rôle facultatifs ; une seule réponse.
import { answerDerogPre, answerDerogPrev, derogPreReferral, derogPreView, optOutDerogPre, type DerogPreView } from '../lib/api';
import {
  REASON_LABEL, ROLES, ROLE_LABEL, dayShort, hm, lowerFirst, matchTitle, ofClub, slotTitle, swapLines, type AnswerRole,
} from '../domain/derogation';
import { swapDatedLines } from '../domain/derogation-suivi';
import { COMPETITION_LABEL } from '../domain/ffbb';
import { derogationSiteUrl } from '../domain/referral';
import { $, $$, html, mount, type Html } from './dom';
import { applyBrand } from './theme';
import './derogations.css';

export async function renderDerogAnswer(root: HTMLElement, token: string, stop: boolean): Promise<void> {
  let v: DerogPreView | null = null;
  // Le lien « Découvrez Arceau » est celui du club qui demande (parrainage, D-087).
  const ref = derogPreReferral(token).catch(() => null);
  try { v = await derogPreView(token, !stop); } catch { /* réseau : même message */ }
  const site = derogationSiteUrl(await ref);
  if (!v) {
    document.title = 'Lien introuvable';
    mount(root, html`<main class="pub-missing"><h1>Lien introuvable</h1>
      <p>Vérifiez le lien reçu, ou contactez directement le club qui vous l’a envoyé.</p></main>`);
    return;
  }
  const view = v;
  const club = view.club;
  const tz = club.timezone || 'Europe/Paris';
  applyBrand({ id: `derog:${token.slice(0, 8)}`, name: club.name, short_name: club.short_name, logo_url: club.logo, primary_color: club.color }, false);
  document.title = `${club.name} · Demande de dérogation`;
  const logo = (cls = 'pub-logo') => (club.logo ? html`<img class="${cls}" src="${club.logo}" alt="Logo du ${club.name}">`
    : html`<span class="${cls} pub-mono" aria-hidden="true">${(club.short_name || club.name).slice(0, 3).toUpperCase()}</span>`);
  const page = (sub: string, body: Html) => mount(root, html`
    <div class="pub dp-pub">
      <header class="pub-head"><div class="pub-id">${logo()}<div><h1>${club.name}</h1><p>${sub}</p></div></div></header>
      <main class="pub-main">${body}</main>
      <footer class="pub-foot">${logo()}<div><strong>${club.name}</strong><small>Réponse sans compte · propulsé par Arceau</small></div></footer>
    </div>`);
  const m = view.match;
  const title = matchTitle(m.team, m.opponent, m.is_home);
  const viral = html`<a class="card dp-viral" href="${site}" target="_blank" rel="noopener">
    <span class="ball" aria-hidden="true"></span>
    <span class="small">Découvrez comment <strong>Arceau</strong> facilite la vie ${ofClub(club.name)}</span>
    <span class="dp-viral-go" aria-hidden="true">→</span></a>`;

  // --- Ne plus recevoir ces messages -----------------------------------------------------
  if (stop) {
    page('Vos préférences', html`
      <section class="pub-sec dp-stop"><h2>Ne plus recevoir ces messages</h2>
        ${view.email_hint ? html`<p>Arceau n’enverra plus aucune demande de dérogation à <strong>${view.email_hint}</strong>, quel que soit le club qui l’envoie.</p>
          <button type="button" class="btn btn-primary" id="dp-stop">Confirmer</button>
          <p class="hint">${club.name} pourra toujours vous joindre directement.</p>`
        : html`<p>Cette demande ne vous a pas été envoyée par e-mail : il n’y a rien à arrêter.</p>`}
      </section>`);
    $(root, '#dp-stop')?.addEventListener('click', async e => {
      const b = e.currentTarget as HTMLButtonElement;
      b.disabled = true;
      const ok = await optOutDerogPre(token).catch(() => false);
      if (!ok) { b.disabled = false; b.textContent = 'Réessayer'; return; }
      page('Vos préférences', html`<section class="pub-thanks"><p class="pub-kicker">✓ C’est noté</p><h3>Plus aucun e-mail</h3>
        <p>Arceau n’écrira plus à ${view.email_hint ?? 'cette adresse'}.</p></section>`);
    });
    return;
  }

  // --- Déjà répondu, retirée, expirée ------------------------------------------------------
  const prev = view.mode === 'prevenir';
  const sub = prev ? 'Changement de rencontre' : 'Demande de dérogation';
  if (prev) document.title = `${club.name} · Changement de rencontre`;
  if (view.answer) { (prev ? prevThanks : thanks)(view, false); return; }
  if (view.status !== 'pending') {
    const closed = view.status === 'closed';
    page(sub, html`<section class="pub-thanks"><p class="pub-kicker">${view.status === 'expired' ? 'Lien expiré' : closed ? 'Demande close' : 'Demande retirée'}</p>
      <h3>${view.status === 'expired' ? 'Trop tard pour répondre' : 'Plus d’actualité'}</h3>
      <p>${view.status === 'expired' ? `Le match ${title} a commencé : ce lien ne vaut plus.` : closed ? 'Cette demande est close. Rien à faire de votre côté.' : `${club.name} a retiré cette demande. Rien à faire de votre côté.`}</p></section>`);
    return;
  }
  if (prev) { renderPrev(); return; }

  // --- Répondre ------------------------------------------------------------------------------
  const swap = view.kind === 'swap';
  const why = view.reason_note ? lowerFirst(view.reason_note) : view.reason === 'autre' ? '' : REASON_LABEL[view.reason].toLowerCase();
  const competition = m.competition_name || COMPETITION_LABEL[m.competition as keyof typeof COMPETITION_LABEL] || '';
  type Pick = number | 'refuse' | 'counter' | null;
  let pick: Pick = null;
  let role: AnswerRole | null = null;
  const slotCard = (i: number, head: string, sub: Html | string, label: string) => html`
    <div class="dp-slot" data-card="${i}"><div><strong>${head}</strong><span class="muted small">${sub}</span></div>
      <button type="button" class="btn btn-ghost btn-sm" data-pick="${i}" data-label="${label}">${label}</button></div>`;
  // D-137 : l'inversion avec de nouvelles dates, l'aller et le retour, « au lieu du… ».
  const swapDated = (sw: NonNullable<DerogPreView['swap']>) => html`
    <div class="dp-slot" data-card="0"><div><strong>Inverser domicile et extérieur, avec de nouvelles dates</strong>
      ${swapDatedLines({ starts_at: m.starts_at, is_home: m.is_home, location: m.location }, view.pair, sw, tz).map(l => html`<span class="dg-publ"><b>${l.head}</b><span>${l.where}</span><small>${l.instead}</small></span>`)}</div>
      <button type="button" class="btn btn-ghost btn-sm" data-pick="0" data-label="D’accord pour l’inversion">D’accord pour l’inversion</button></div>`;
  page('Demande de dérogation', html`
    <section class="pub-hero"><p class="pub-kicker">${swap ? 'Rencontre à inverser' : 'Rencontre à déplacer'}</p><h2>${title}</h2>
      <p class="pub-when">${dayShort(m.starts_at, tz)} · ${hm(m.starts_at, tz)}${m.location ? ` · ${m.location}` : ''}</p>
      <p class="pub-sub">${[competition, why ? `pourquoi : ${why}` : ''].filter(Boolean).join(' · ')}</p></section>
    <section class="pub-sec"><h2>${swap ? 'L’inversion proposée' : 'Les créneaux proposés'}</h2>
      ${swap && view.swap ? swapDated(view.swap)
        : swap ? slotCard(0, 'Inverser domicile et extérieur', html`${swapLines({ starts_at: m.starts_at, is_home: m.is_home }, view.pair, tz).join(' · ')}<br>Mêmes dates`, 'D’accord pour l’inversion')
        : view.slots.map((s, i) => slotCard(i, slotTitle(s.starts_at, tz), s.location ?? 'Salle inchangée', 'D’accord pour ce créneau'))}
      <div class="btn-row"><button type="button" class="btn btn-ghost btn-sm" data-pick="refuse" data-label="Pas possible">Pas possible</button>
        <button type="button" class="btn btn-ghost btn-sm" data-pick="counter" data-label="Proposer autre chose">Proposer autre chose</button></div>
      <div class="call-slot dp-counter" id="dp-counter" hidden><span class="eyebrow">Votre proposition</span>
        <div class="call-slot-when"><input type="date" name="cd" aria-label="Date proposée"><input type="time" name="ct" aria-label="Heure proposée"></div>
        <input name="label" class="dp-full" maxlength="200" placeholder="Salle (facultatif)" aria-label="Salle proposée">
        <span class="muted small">Une seule contre-proposition : ensuite, voyez-vous directement avec le club.</span></div>
    </section>
    <section class="pub-sec" id="dp-answer"><h2>Votre réponse</h2>
      <form class="form" id="f-answer" novalidate>
        <label class="field"><span>Votre nom</span><input name="name" maxlength="80" autocomplete="name" required></label>
        <div class="field"><span>Votre rôle à ${view.opponent_club}</span>
          <div class="chip-toggles" role="radiogroup" aria-label="Votre rôle">${ROLES.map(r => html`
            <button type="button" class="chip-toggle" role="radio" data-role="${r}" aria-pressed="false">${ROLE_LABEL[r]}</button>`)}</div></div>
        <label class="field"><span id="dp-msg-label">Un mot (facultatif)</span><textarea name="message" rows="2" maxlength="500"></textarea></label>
        <p class="form-msg" role="alert"></p>
        <button class="btn btn-primary" type="submit">Envoyer la réponse</button>
        <p class="hint">Sans compte. Votre réponse va au bureau ${ofClub(club.name)}, qui saisit ensuite la demande sur FBI.</p>
      </form>
    </section>`);

  const f = $<HTMLFormElement>(root, '#f-answer')!;
  const msg = $(f, '.form-msg')!;
  for (const b of $$<HTMLButtonElement>(root, '[data-pick]')) b.addEventListener('click', () => {
    const p = b.dataset.pick!;
    pick = p === 'refuse' || p === 'counter' ? p : Number(p);
    for (const x of $$<HTMLButtonElement>(root, '[data-pick]')) {
      const on = x === b;
      x.classList.toggle('btn-primary', on); x.classList.toggle('btn-ghost', !on);
      x.textContent = `${on && x.dataset.pick !== 'refuse' && x.dataset.pick !== 'counter' ? '✓ ' : ''}${x.dataset.label}`;
      x.setAttribute('aria-pressed', String(on));
      x.closest('.dp-slot')?.classList.toggle('dp-slot-on', on);
    }
    $(root, '#dp-counter')!.hidden = pick !== 'counter';
    $(f, '#dp-msg-label')!.textContent = pick === 'refuse' ? 'Pourquoi (facultatif)' : 'Un mot (facultatif)';
    msg.textContent = '';
    if (pick === 'counter') $<HTMLInputElement>(root, '#dp-counter input[type="date"]')!.focus();
  });
  for (const b of $$<HTMLButtonElement>(f, '[data-role]')) b.addEventListener('click', () => {
    role = b.dataset.role as AnswerRole;
    $$(f, '[data-role]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
  });
  f.addEventListener('submit', async e => {
    e.preventDefault();
    const d = new FormData(f);
    const name = String(d.get('name') ?? '').trim();
    const message = String(d.get('message') ?? '').trim() || null;
    if (pick === null) { msg.textContent = 'Choisissez un créneau, « Pas possible » ou « Proposer autre chose ».'; return; }
    let counter: { starts_at: string; location: string | null } | null = null;
    if (pick === 'counter') {
      const box = $(root, '#dp-counter')!;
      const cd = $<HTMLInputElement>(box, 'input[type="date"]')!.value, ct = $<HTMLInputElement>(box, 'input[type="time"]')!.value;
      const at = new Date(`${cd}T${ct}:00`);
      if (!cd || !ct || isNaN(at.getTime())) { msg.textContent = 'Indiquez la date et l’heure que vous proposez.'; return; }
      if (at <= new Date()) { msg.textContent = 'La date proposée doit être à venir.'; return; }
      counter = { starts_at: at.toISOString(), location: $<HTMLInputElement>(box, 'input[name="label"]')!.value.trim() || null };
    }
    if (!name) { msg.textContent = 'Votre nom, s’il vous plaît.'; $<HTMLInputElement>(f, 'input[name="name"]')!.focus(); return; }
    if (!role) { msg.textContent = 'Votre rôle au club, s’il vous plaît.'; return; }
    const btn = $<HTMLButtonElement>(f, 'button[type="submit"]')!;
    btn.disabled = true;
    try {
      await answerDerogPre(token, { kind: typeof pick === 'number' ? 'accept' : pick, slot: typeof pick === 'number' && !swap ? pick : null, counter, name, role, message });
      const after = await derogPreView(token, false).catch(() => null);
      if (after?.answer) thanks(after, true); else location.reload();
    } catch (err) {
      const t = String((err as Error)?.message ?? '');
      if (t.includes('déjà') || t.includes('retirée') || t.includes('expiré')) { void renderDerogAnswer(root, token, false); return; }
      msg.textContent = 'Envoi impossible pour le moment. Réessayez dans un instant.';
      btn.disabled = false;
    }
  });

  // --- Merci --------------------------------------------------------------------------------
  function thanks(x: DerogPreView, fresh: boolean): void {
    const a = x.answer!;
    const s = a.slot !== null ? x.slots[a.slot] : null;
    const what = a.kind === 'refuse' ? 'Pas possible'
      : a.kind === 'counter' && a.counter ? `Vous proposez le ${dayShort(a.counter.starts_at, tz)} à ${hm(a.counter.starts_at, tz)}`
      : x.kind === 'swap' ? 'D’accord pour l’inversion domicile / extérieur'
      : s ? `D’accord pour le ${dayShort(s.starts_at, tz)} à ${hm(s.starts_at, tz)}` : 'D’accord';
    const where = a.kind === 'counter' ? a.counter?.location : s?.location;
    const line = a.kind === 'accept' ? `${club.name} saisit la demande sur FBI.`
      : a.kind === 'refuse' ? `${club.name} est prévenu de votre réponse.` : `${club.name} étudie votre proposition.`;
    const next = a.kind === 'accept' ? 'La demande officielle suivra dans FBI.'
      : a.kind === 'counter' ? 'Une seule contre-proposition par demande : la suite se règle directement entre clubs.'
      : 'Le club pourra revenir vers vous avec d’autres créneaux.';
    page('Demande de dérogation', html`
      <section class="pub-thanks"><p class="pub-kicker">✓ ${fresh ? 'Réponse envoyée' : 'Réponse déjà envoyée'}</p><h3>Merci !</h3><p>${line}</p></section>
      <section class="card dp-recap"><p class="pub-kicker">Votre réponse</p>
        <strong>${what}</strong>
        <span class="muted small">${[where, title].filter(Boolean).join(' · ')}</span>
        <span class="small">${[a.name, a.role ? ROLE_LABEL[a.role].toLowerCase() : null].filter(Boolean).join(', ')}${a.message ? ` · « ${a.message} »` : ''}</span>
        <span class="muted small">${next}</span></section>
      ${viral}`);
    window.scrollTo(0, 0);
  }

  // --- D-146, p4 et p4b : « Les prévenir » : le changement annoncé, « Bien reçu » ou « Ça nous pose problème » -------
  function renderPrev(): void {
    const s = view.slots[0];
    const why = view.reason_note ? lowerFirst(view.reason_note) : view.reason === 'autre' ? '' : REASON_LABEL[view.reason].toLowerCase();
    const competition = m.competition_name || COMPETITION_LABEL[m.competition as keyof typeof COMPETITION_LABEL] || '';
    const moved = !!s && new Date(s.starts_at).getTime() !== new Date(m.starts_at).getTime();
    const sameDay = !!s && dayShort(s.starts_at, tz) === dayShort(m.starts_at, tz);
    const hall = !!s?.location && s.location !== m.location;
    page(sub, html`
      <section class="pub-hero"><p class="pub-kicker">Changement annoncé</p><h2>${title}</h2>
        <p class="pub-when">${dayShort(m.starts_at, tz)} · ${hm(m.starts_at, tz)}${m.location ? ` · ${m.location}` : ''}</p>
        <p class="pub-sub">${[competition, why ? `pourquoi : ${why}` : ''].filter(Boolean).join(' · ')}</p></section>
      <section class="pub-sec"><h2>Le changement</h2>
        ${s ? html`<div class="dp-slot dz-pub-change"><div><strong>${slotTitle(s.starts_at, tz)}</strong>
          <span class="muted small">${hall ? s.location : `${s.location ?? m.location ?? ''}${s.location || m.location ? ', ' : ''}même salle`}</span>
          <small>${moved ? `au lieu de ${hm(m.starts_at, tz)}${sameDay ? '' : ` le ${dayShort(m.starts_at, tz)}`}` : ''}${moved && hall ? ', ' : ''}${hall ? `${moved ? 'à' : 'au lieu de'} ${m.location ?? 'la salle prévue'}` : ''}</small></div></div>` : ''}
        <p class="dz-pub-note">${ofClub(club.name).replace(/^du /, 'Le ').replace(/^de l’/, 'L’')} saisit ce changement sur FBI. Rien à faire de votre côté, sauf si FBI vous demande votre accord.</p></section>
      <section class="pub-sec" id="dp-answer"><h2>Votre réponse</h2>
        <form class="form" id="f-prev" novalidate>
          <label class="field"><span>Votre nom et votre rôle (facultatif)</span><input name="name" maxlength="80" autocomplete="name" placeholder="ex. Hélène, secrétariat"></label>
          <div class="dz-pub-btns">
            <button type="button" class="btn btn-primary" id="dz-ack">Bien reçu</button>
            <button type="button" class="btn btn-ghost" id="dz-pb" aria-pressed="false">Ça nous pose problème</button></div>
          <div id="dz-pb-box" class="form" hidden>
            <label class="field"><span>Dites-nous pourquoi, en un mot</span><textarea name="message" rows="3" maxlength="500"></textarea></label>
            <button type="submit" class="btn btn-primary">Envoyer</button>
            <p class="hint">Un mot suffit : ${ofClub(club.name).replace(/^du /, 'le ').replace(/^de l’/, 'l’')} est prévenu et revient vers vous.</p></div>
          <p class="form-msg" role="alert"></p>
          <p class="hint" id="dz-ack-hint">Sans compte, en un geste. Votre réponse va au bureau ${ofClub(club.name)}.</p>
        </form></section>`);
    const f = $<HTMLFormElement>(root, '#f-prev')!;
    const msg = $(f, '.form-msg')!;
    const ack = $<HTMLButtonElement>(f, '#dz-ack')!, pb = $<HTMLButtonElement>(f, '#dz-pb')!;
    const send = async (ok: boolean) => {
      const d = new FormData(f);
      const name = String(d.get('name') ?? '').trim() || null;
      const message = String(d.get('message') ?? '').trim() || null;
      if (!ok && !message) { msg.textContent = 'Dites-nous pourquoi, en un mot.'; $<HTMLTextAreaElement>(f, 'textarea')!.focus(); return; }
      msg.textContent = '';
      $$<HTMLButtonElement>(f, 'button').forEach(b => { b.disabled = true; });
      try {
        await answerDerogPrev(token, ok, name, message);
        const after = await derogPreView(token, false).catch(() => null);
        if (after?.answer) prevThanks(after, true); else location.reload();
      } catch (err) {
        const t = String((err as Error)?.message ?? '');
        if (t.includes('déjà') || t.includes('retirée') || t.includes('expiré')) { void renderDerogAnswer(root, token, false); return; }
        msg.textContent = 'Envoi impossible pour le moment. Réessayez dans un instant.';
        $$<HTMLButtonElement>(f, 'button').forEach(b => { b.disabled = false; });
      }
    };
    ack.addEventListener('click', () => void send(true));
    pb.addEventListener('click', () => {
      const on = pb.getAttribute('aria-pressed') !== 'true';
      pb.setAttribute('aria-pressed', String(on));
      pb.textContent = `${on ? '✓ ' : ''}Ça nous pose problème`;
      pb.classList.toggle('btn-primary', on); pb.classList.toggle('btn-ghost', !on);
      ack.classList.toggle('btn-primary', !on); ack.classList.toggle('btn-ghost', on);
      $(f, '#dz-pb-box')!.hidden = !on;
      $(f, '#dz-ack-hint')!.hidden = on;
      if (on) $<HTMLTextAreaElement>(f, 'textarea')!.focus();
    });
    f.addEventListener('submit', e => { e.preventDefault(); void send(false); });
  }

  // --- D-146, p4c : merci (« Bien reçu » ou « Ça nous pose problème ») ------------------------------------------------
  function prevThanks(x: DerogPreView, fresh: boolean): void {
    const a = x.answer!;
    const s = x.slots[0];
    const ok = a.kind === 'ack';
    const who = (a.name ?? '').trim();
    page(sub, html`
      <section class="pub-thanks"><p class="pub-kicker">✓ ${fresh ? 'Réponse envoyée' : 'Réponse déjà envoyée'}</p><h3>Merci !</h3>
        <p>${ok ? `${ofClub(club.name).replace(/^du /, 'Le ').replace(/^de l’/, 'L’')} saisit le changement sur FBI.` : `${ofClub(club.name).replace(/^du /, 'Le ').replace(/^de l’/, 'L’')} est prévenu et revient vers vous.`}</p></section>
      <section class="card dp-recap"><p class="pub-kicker">Votre réponse</p>
        <strong>${ok ? 'Bien reçu' : 'Ça nous pose problème'}</strong>
        <span class="muted small">${[s ? slotTitle(s.starts_at, tz) : '', s?.location ?? '', title].filter(Boolean).join(' · ')}</span>
        ${who ? html`<span class="small">${who}</span>` : ''}
        ${!ok && a.message ? html`<span class="small">« ${a.message} »</span>` : ''}
        <span class="muted small">${x.email_hint ? 'Un souci plus tard ? Répondez simplement à l’e-mail : il arrive au bureau du club.' : 'Un souci plus tard ? Contactez directement le club.'}</span></section>
      ${viral}`);
    window.scrollTo(0, 0);
  }
}
